import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { displayDate, money, moneyWhole, toDateInput } from './registerFormat';
import {
  FilterCount,
  MultiSelect,
  SearchBox,
  anyFilterSet,
  matchesSelection,
} from './registerFilters';
import { useRegisterDirty } from '../unsavedChangesGuard';

// Mirrors CHANGE_REGISTER_STATUSES in packages/shared -- apps/web can't import it (ADR 0019), and
// this vocabulary is small and stable enough not to be worth a round trip to populate a dropdown.
export const CHANGE_REGISTER_STATUSES = [
  'Draft', 'Submitted', 'Approved', 'DAS Submitted', 'DAS Approved', 'Paid', 'Rejected', 'Canceled',
];

// Gray while it's still ours to finish, blue once it's with the customer, green once it's settled,
// red when it isn't happening.
export const CHANGE_STATUS_BADGE = {
  Draft: 'badge-gray',
  Submitted: 'badge-blue',
  Approved: 'badge-blue',
  'DAS Submitted': 'badge-yellow',
  'DAS Approved': 'badge-yellow',
  Paid: 'badge-green',
  Rejected: 'badge-red',
  Canceled: 'badge-gray',
};

// Mirrors isSettledChangeStatus in packages/shared. A set rather than a `!== 'Paid'` test so
// widening the vocabulary can't silently start counting a settled CR as in flight.
const SETTLED_STATUSES = new Set(['Paid', 'Rejected', 'Canceled']);

export function isSettled(status) {
  return SETTLED_STATUSES.has(status);
}

// Sentinel for the one status option that isn't a plain equality test. Prefixed so it can't collide
// with a real status.
export const IN_FLIGHT_FILTER = '__inflight';

// Plural key, array value: every filter on these pages is multi-select (Jeremy, 2026-09-03), and an
// empty array means "no filter" rather than "nothing matches".
export const BLANK_CHANGE_FILTERS = { statuses: [], text: '' };

const FILTER_ARRAY_KEYS = ['statuses'];

const SEARCH_FIELDS = ['description', 'notes'];

export function filterChangeItems(items, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  return (items || []).filter((item) => {
    if (!matchesSelection(filters.statuses, item.status, item, [
      { value: IN_FLIGHT_FILTER, matches: (r) => !isSettled(r.status) },
    ])) return false;
    if (needle && !SEARCH_FIELDS.some((f) => (item[f] || '').toLowerCase().includes(needle))) {
      return false;
    }
    return true;
  });
}

export function isChangeFiltered(filters) {
  return anyFilterSet(filters, FILTER_ARRAY_KEYS);
}

export const CHANGE_SORT_VALUE = {
  crNumber: (r) => r.crNumber,
  description: (r) => lower(r.description),
  // -Infinity, not 0: an amount that hasn't been agreed sorts below a genuine $0 rather than tying
  // with it.
  billingAmount: (r) => r.billingAmount ?? -Infinity,
  status: (r) => lower(r.status),
  crSubmittedDate: (r) => r.crSubmittedDate ?? '',
  dasSubmittedDate: (r) => r.dasSubmittedDate ?? '',
};

const BLANK_ITEM = { description: 'New change request', status: 'Draft' };

// `billingAmount`, the five dates and `notes` distinguish "clear it" (explicit null on update) from
// "leave it alone" (omitted) -- see UpdateChangeRegisterItemDto. `row` is a live grid row (edited in
// place), not a separate draft.
function buildPayload(row) {
  const dateOrClear = (v) => (v ? v : null);
  // Rounded to 2dp: the API's @IsNumber({ maxDecimalPlaces: 2 }) rejects a typed "1000.005", and
  // nothing else stops a third decimal being pasted into the amount box.
  const amount = row.billingAmount === '' || row.billingAmount == null
    ? null
    : Math.round(Number(row.billingAmount) * 100) / 100;
  return {
    description: row.description.trim(),
    billingAmount: amount,
    status: row.status,
    crSubmittedDate: dateOrClear(toDateInput(row.crSubmittedDate)),
    crApprovedDate: dateOrClear(toDateInput(row.crApprovedDate)),
    dasSubmittedDate: dateOrClear(toDateInput(row.dasSubmittedDate)),
    dasApprovedDate: dateOrClear(toDateInput(row.dasApprovedDate)),
    datePaid: dateOrClear(toDateInput(row.datePaid)),
    notes: (row.notes || '').trim() === '' ? null : row.notes.trim(),
  };
}

/**
 * A project's change (CR) register -- replaces "Change Control Log_<customer>.xlsx". Customer-visible
 * in full: a CR register is the record of what the customer agreed to change, and the log it
 * replaces is shared with them.
 *
 * Not a change-control approval workflow: `ccb` was ruled out as a module (CLAUDE.md Section 4, "Won't
 * build, 2026-07-30"). This records CRs that happened, it doesn't route them for approval.
 *
 * Cells edit in place; Add/Save live on the grid's own toolbar and a row's ✕ removes it. See
 * ProjectContactList.js for the fuller write-up of this convention.
 *
 * One tab of ProjectRegisters.js, which owns the page header and the project picker. Also rendered
 * by CustomerProjectRegisters.js for a customer contact with edit rights on this project (item 5,
 * 2026-09-12) -- `readOnly` collapses the toolbar and the grid's write affordances.
 */
export default function ChangeRegister({ projectId, onCoverageChange, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ ...BLANK_CHANGE_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [sort, toggleSort] = useGridSort('crNumber');

  useRegisterDirty(dirtyIds.size > 0);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch(`/change-register-items?projectId=${projectId}`)
      .then((rows) => {
        setItems(rows);
        onCoverageChange?.(rows.length > 0);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterChangeItems(items, filters), [items, filters]);
  const sorted = useMemo(
    () => sortRows(filtered, sort, (row, key) => CHANGE_SORT_VALUE[key](row)),
    [filtered, sort],
  );

  function editCell(id, key, value) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [key]: value } : i)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Add and delete reload the grid from the server, which would discard other rows' unsaved edits,
  // so save them first -- same as Project Budgets' Add Line.
  async function flushDirty(skipId) {
    const ids = [...dirtyIds].filter((id) => id !== skipId);
    if (!ids.length) return;
    await Promise.all(ids.map((id) => apiFetch(`/change-register-items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(buildPayload(items.find((r) => r.id === id))),
    })));
    setDirtyIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
  }

  async function addItem() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/change-register-items', {
        method: 'POST',
        body: JSON.stringify({ ...BLANK_ITEM, projectId }),
      });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await flushDirty();
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteItem(id) {
    setActionError('');
    try {
      await flushDirty(id);
      await apiFetch(`/change-register-items/${id}`, { method: 'DELETE' });
      // A dirty edit on this row is now moot -- left in place it would keep the unsaved-changes
      // guard tripped forever, and a later Save would PATCH an id that no longer exists.
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      {/* Not gated on `!loading`: the refetch after every save would otherwise unmount the cards and
          the filter bar and collapse the page. */}
      {!!items.length && (
        <>
          <ChangeSummary items={items} />
          <div className="card" style={{ marginBottom: 16 }}>
            <ChangeFilters filters={filters} onChange={setFilters} />
          </div>
        </>
      )}

      <div className="card">
        {!readOnly && (
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <div className="spacer" />
            <button className="btn btn-outline" onClick={addItem} disabled={!!busy}>
              {busy === 'adding' ? 'Adding…' : '+ Add change request'}
            </button>
            <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
              {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
            </button>
          </div>
        )}
        {loading ? (
          <div style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
        ) : !items.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">📝</div>
            <div className="placeholder-title">No change requests yet</div>
            <div className="placeholder-sub">
              {readOnly ? 'No change requests have been logged for this project yet.' : 'Log the first CR against this project above.'}
            </div>
          </div>
        ) : !sorted.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔍</div>
            <div className="placeholder-title">No change requests match these filters</div>
            <div className="placeholder-sub">
              {items.length} {items.length === 1 ? 'CR is' : 'CRs are'} logged against this project —
              clear the filters to see them.
            </div>
          </div>
        ) : (
          <ChangeGrid
            items={sorted}
            total={items.length}
            filtered={isChangeFiltered(filters)}
            sort={sort}
            onSort={toggleSort}
            expandedId={expandedId}
            onToggleExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
            dirtyIds={dirtyIds}
            onEdit={editCell}
            onDelete={deleteItem}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}

const COLUMN_COUNT = 8;

/**
 * Exported: the customer portal renders the identical grid with `readOnly`, so the two views can't
 * drift on columns or on which fields hide behind the expand row.
 */
export function ChangeGrid({
  items, total, filtered, sort, onSort, expandedId, onToggleExpand, dirtyIds, onEdit, onDelete, readOnly,
}) {
  const [confirmId, setConfirmId] = useState('');

  return (
    <>
      {filtered && <FilterCount shown={items.length} total={total} noun="CR" />}
      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <SortTh label="CR #" sortKey="crNumber" sort={sort} onSort={onSort} style={{ width: 60 }} />
              <SortTh label="Description" sortKey="description" sort={sort} onSort={onSort} style={{ minWidth: 300 }} />
              <SortTh label="Billing amount" sortKey="billingAmount" sort={sort} onSort={onSort} style={{ width: 130 }} />
              <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} style={{ width: 130 }} />
              <SortTh label="CR submitted" sortKey="crSubmittedDate" sort={sort} onSort={onSort} style={{ width: 120 }} />
              <SortTh label="DAS submitted" sortKey="dasSubmittedDate" sort={sort} onSort={onSort} style={{ width: 120 }} />
              <th style={{ width: readOnly ? 12 : 36 }} />
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <React.Fragment key={item.id}>
                <tr className={rowClassName(i, dirtyIds?.has(item.id))}>
                  <td className="cell-check">
                    <button
                      onClick={() => onToggleExpand(item.id)}
                      title={expandedId === item.id ? 'Collapse' : 'Show detail'}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-mid)' }}
                    >
                      {expandedId === item.id ? '▾' : '▸'}
                    </button>
                  </td>
                  <td className="cell-num">{item.crNumber}</td>
                  {readOnly ? (
                    // pre-wrap: the source log's descriptions are multi-line bullet lists of the SOW
                    // items a CR touches, and collapsing them to one line makes them unreadable.
                    <td className="cell-read" style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>{item.description}</td>
                  ) : (
                    <td><AutoGrowTextarea value={item.description} onChange={(e) => onEdit(item.id, 'description', e.target.value)} /></td>
                  )}
                  {readOnly ? (
                    <td className="cell-num">{money(item.billingAmount)}</td>
                  ) : (
                    <td>
                      <input
                        type="number" step="0.01" className="cell-input" style={{ textAlign: 'right' }}
                        value={item.billingAmount ?? ''} onChange={(e) => onEdit(item.id, 'billingAmount', e.target.value)}
                      />
                    </td>
                  )}
                  {readOnly ? (
                    <td className="cell-read">
                      <span className={`badge ${CHANGE_STATUS_BADGE[item.status] || 'badge-gray'}`}>{item.status}</span>
                    </td>
                  ) : (
                    <td>
                      <select className="cell-select" value={item.status} onChange={(e) => onEdit(item.id, 'status', e.target.value)}>
                        {CHANGE_REGISTER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  )}
                  {readOnly ? (
                    <td className="cell-read">{displayDate(item.crSubmittedDate)}</td>
                  ) : (
                    <td><input type="date" className="cell-input" value={toDateInput(item.crSubmittedDate)} onChange={(e) => onEdit(item.id, 'crSubmittedDate', e.target.value)} /></td>
                  )}
                  {readOnly ? (
                    <td className="cell-read">{displayDate(item.dasSubmittedDate)}</td>
                  ) : (
                    <td><input type="date" className="cell-input" value={toDateInput(item.dasSubmittedDate)} onChange={(e) => onEdit(item.id, 'dasSubmittedDate', e.target.value)} /></td>
                  )}
                  <td className="cell-check">
                    {!readOnly && (
                      confirmId === item.id ? (
                        <button className="icon-btn" onClick={() => { setConfirmId(''); onDelete(item.id); }} title="Confirm delete — can't be undone">✓?</button>
                      ) : (
                        <button className="icon-btn" onClick={() => setConfirmId(item.id)} title="Delete this CR">✕</button>
                      )
                    )}
                  </td>
                </tr>
                {expandedId === item.id && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} style={{ background: 'var(--light-bg)' }}>
                      <ChangeDetailPanel item={item} onEdit={onEdit} readOnly={readOnly} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * The expand panel, reorganized (item 2 of the 2026-09-12 register rework) into two labeled groups
 * — Dates, then Notes — instead of one undifferentiated field grid, so it reads the same regardless
 * of which fields happen to be filled in.
 */
function ChangeDetailPanel({ item, onEdit, readOnly }) {
  return (
    <div style={{ padding: '12px 8px' }}>
      <DetailGroup label="Dates">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          <DetailField label="CR approved">
            {readOnly ? displayDate(item.crApprovedDate) : (
              <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.crApprovedDate)} onChange={(e) => onEdit(item.id, 'crApprovedDate', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="DAS approved">
            {readOnly ? displayDate(item.dasApprovedDate) : (
              <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.dasApprovedDate)} onChange={(e) => onEdit(item.id, 'dasApprovedDate', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Date paid">
            {readOnly ? displayDate(item.datePaid) : (
              <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.datePaid)} onChange={(e) => onEdit(item.id, 'datePaid', e.target.value)} />
            )}
          </DetailField>
        </div>
      </DetailGroup>
      <DetailGroup label="Notes" style={{ marginTop: 16 }}>
        {readOnly ? (
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{item.notes || '—'}</div>
        ) : (
          <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.notes || ''} onChange={(e) => onEdit(item.id, 'notes', e.target.value)} />
        )}
      </DetailGroup>
    </div>
  );
}

function DetailGroup({ label, children, style }) {
  return (
    <div style={style}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 8 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}

/**
 * Exported for the customer view, so both sides count the register the same way. Counts describe the
 * whole register, not the filtered subset -- the grid reports its own filtered count.
 */
export function ChangeSummary({ items }) {
  const inFlight = items.filter((i) => !isSettled(i.status));
  // Only the CRs that will actually be billed: a rejected or cancelled CR's amount never lands, and
  // including it would overstate the change value on a page a customer reads. Credits (negative
  // amounts) net off, which is the whole point of the figure.
  const netAmount = items
    .filter((i) => i.status !== 'Rejected' && i.status !== 'Canceled')
    .reduce((sum, i) => sum + (i.billingAmount ?? 0), 0);
  return (
    <div className="stat-row">
      <div className="stat">
        <div className="stat-value">{items.length}</div>
        <div className="stat-label">Total CRs</div>
      </div>
      <div className={`stat${inFlight.length ? ' is-alert' : ''}`}>
        <div className="stat-value">{inFlight.length}</div>
        <div className="stat-label">In flight</div>
      </div>
      <div className="stat">
        <div className="stat-value">{moneyWhole(netAmount)}</div>
        <div className="stat-label">Net billing impact</div>
      </div>
    </div>
  );
}

export function ChangeFilters({ filters, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <MultiSelect
        label="Status"
        allLabel="All statuses"
        options={[
          // Ahead of the individual statuses because "what's still moving" is the question the
          // register gets asked most, and it spans five of the eight values.
          { value: IN_FLIGHT_FILTER, label: 'In flight (any)' },
          ...CHANGE_REGISTER_STATUSES.map((s) => ({ value: s, label: s })),
        ]}
        selected={filters.statuses}
        onChange={(statuses) => onChange({ ...filters, statuses })}
        minWidth={180}
      />
      <SearchBox
        value={filters.text}
        onChange={(text) => onChange({ ...filters, text })}
        placeholder="Description, notes…"
      />
      {isChangeFiltered(filters) && (
        <button className="btn btn-outline" onClick={() => onChange({ ...BLANK_CHANGE_FILTERS })}>
          Clear filters
        </button>
      )}
    </div>
  );
}
